<template>
  <nav class="navbar">
    <div class="navbar-logo">
      <img src="../assets/logo.png" alt="Eduval Logo" class="logo">
      <span class="company-name">By Academics, for Academics</span>
    </div>
    <div class="navbar-links">
      <a href="#hero" class="nav-link" :class="{ active: activeSection === 'hero' }" @click.prevent="scrollToSection('hero')">Home</a>
      <a href="#lifecycle" class="nav-link" :class="{ active: activeSection === 'lifecycle' }" @click.prevent="scrollToSection('lifecycle')">Standards</a>
      <a href="#about-us" class="nav-link" :class="{ active: activeSection === 'about-us' }" @click.prevent="scrollToSection('about-us')">About Us</a>
      <a href="#services" class="nav-link" :class="{ active: activeSection === 'services' }" @click.prevent="scrollToSection('services')">Services</a>
      <a href="#products" class="nav-link" :class="{ active: activeSection === 'products' }" @click.prevent="scrollToSection('products')">Products</a>
      <a href="#partners" class="nav-link" :class="{ active: activeSection === 'partners' }" @click.prevent="scrollToSection('partners')">Partners</a>
      <a href="#faq" class="nav-link" :class="{ active: activeSection === 'faq' }" @click.prevent="scrollToSection('faq')">FAQ</a>
      <a href="#contact" class="nav-link" :class="{ active: activeSection === 'contact' }" @click.prevent="scrollToSection('contact')">Contact</a>
      <a
        class="portal-nav-btn"
        href="https://support.eduval.net"
        target="_blank"
        rel="noopener noreferrer"
      >
        <i class="fas fa-user-circle"></i>
        <span>Client Portal</span>
      </a>
      <button class="demo-nav-btn" @click="requestDemo">
        <i class="fas fa-headset"></i>
        Request a Demo
      </button>
    </div>
    <button class="menu-toggle" @click="toggleMobileMenu">
      <span></span>
      <span></span>
      <span></span>
    </button>
    <div class="mobile-menu" :class="{ 'active': mobileMenuOpen }">
      <a href="#hero" class="mobile-link" :class="{ active: activeSection === 'hero' }" @click.prevent="scrollToSection('hero', true)">Home</a>
      <a href="#lifecycle" class="mobile-link" :class="{ active: activeSection === 'lifecycle' }" @click.prevent="scrollToSection('lifecycle', true)">Standards</a>
      <a href="#about-us" class="mobile-link" :class="{ active: activeSection === 'about-us' }" @click.prevent="scrollToSection('about-us', true)">About Us</a>
      <a href="#services" class="mobile-link" :class="{ active: activeSection === 'services' }" @click.prevent="scrollToSection('services', true)">Services</a>
      <a href="#products" class="mobile-link" :class="{ active: activeSection === 'products' }" @click.prevent="scrollToSection('products', true)">Products</a>
      <a href="#partners" class="mobile-link" :class="{ active: activeSection === 'partners' }" @click.prevent="scrollToSection('partners', true)">Partners</a>
      <a href="#faq" class="mobile-link" :class="{ active: activeSection === 'faq' }" @click.prevent="scrollToSection('faq', true)">FAQ</a>
      <a href="#contact" class="mobile-link" :class="{ active: activeSection === 'contact' }" @click.prevent="scrollToSection('contact', true)">Contact</a>
      <div class="mobile-cta-group">
        <a
          class="portal-mobile-btn"
          href="https://support.eduval.net"
          target="_blank"
          rel="noopener noreferrer"
          @click="closeMobileMenu"
        >
          <i class="fas fa-user-circle"></i>
          <span>Client Portal</span>
        </a>
        <button class="demo-mobile-btn" @click="requestDemo">
          <i class="fas fa-headset"></i>
          Request a Demo
        </button>
      </div>
    </div>
  </nav>
</template>

<script>
export default {
  name: 'NavBarTop',
  data() {
    return {
      mobileMenuOpen: false,
      activeSection: 'hero',
      isScrolling: false
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll);
    // Initialize active section
    this.onScroll();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    requestDemo() {
      // Close mobile menu if open
      if (this.mobileMenuOpen) {
        this.closeMobileMenu();
      }
      
      // Emit an event that will be caught by the parent
      this.$emit('request-demo');
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
      document.body.style.overflow = this.mobileMenuOpen ? 'hidden' : '';
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
      document.body.style.overflow = '';
    },
    scrollToSection(sectionId, isMobile = false) {
      // If mobile menu is open, close it
      if (isMobile) {
        this.closeMobileMenu();
      }
      
      // Find the target section
      const element = document.getElementById(sectionId);
      if (!element) return;
      
      // Set flag to prevent onScroll from changing activeSection during animation
      this.isScrolling = true;
      
      // Update active section immediately for better UX
      this.activeSection = sectionId;
      
      // Create a highlight effect on the target section
      element.classList.add('highlight-section');
      
      // Get navbar height to offset scroll position
      const navbar = document.querySelector('.navbar');
      const navbarHeight = navbar ? navbar.offsetHeight : 0;
      
      // Calculate the element's position and adjust for navbar height
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight - 20; // Extra 20px buffer
      
      // Scroll to the adjusted position
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      // Remove highlight after animation completes
      setTimeout(() => {
        this.isScrolling = false;
        element.classList.remove('highlight-section');
      }, 1000);
    },
    onScroll() {
      // Don't update active section during programmatic scrolling
      if (this.isScrolling) return;
      
      // Get all sections
      const sections = [
        'hero',
        'lifecycle',
        'about-us',
        'products',
        'services',
        'partners',
        'faq',
        'contact'
      ];
      
      // Find which section is currently most visible in the viewport
      let current = '';
      let maxVisibility = 0;
      
      sections.forEach(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          
          // Calculate how much of the section is visible (as a percentage of the viewport)
          const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
          const visibilityPercent = visibleHeight > 0 ? visibleHeight / viewportHeight : 0;
          
          if (visibilityPercent > maxVisibility) {
            maxVisibility = visibilityPercent;
            current = sectionId;
          }
        }
      });
      
      // Update active section
      if (current && this.activeSection !== current) {
        this.activeSection = current;
      }
    }
  }
}
</script>

<style scoped>
.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 15px rgba(209, 192, 110, 0.2);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  transition: all 0.3s ease-in-out;
}

.navbar:hover {
  box-shadow: 0 4px 20px rgba(209, 192, 110, 0.3);
}

.navbar-logo {
  display: flex;
  align-items: center;
  animation: fadeInLeft 0.8s ease-out;
}

.logo {
  height: 80px;
  margin-right: 0.5rem;
  transition: transform 0.3s ease;
}

.logo:hover {
  transform: scale(1.1) rotate(5deg);
}

.company-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #d1c06e;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

.company-name:hover {
  color: #beab4f;
  letter-spacing: 0.5px;
}

.navbar-links {
  display: flex;
  gap: 1.5rem;
  animation: fadeInRight 0.8s ease-out;
}

.nav-link {
  text-decoration: none;
  color: #333;
  font-weight: 600;
  padding: 0.6rem 1rem;
  position: relative;
  transition: all 0.3s;
  border-radius: 20px;
  overflow: hidden;
  letter-spacing: 0.5px;
  z-index: 1;
}

.nav-link:before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg, #d1c06e, #e0d394);
  opacity: 0;
  transform: translateY(100%);
  transition: all 0.3s ease;
  z-index: -1;
  border-radius: 20px;
}

.nav-link:hover:before, .nav-link.active:before {
  transform: translateY(0);
  opacity: 1;
}

.nav-link:hover, .nav-link.active {
  color: #fff;
  box-shadow: 0 4px 15px rgba(209, 192, 110, 0.4);
  transform: translateY(-3px);
}

.menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  height: 22px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 101;
}

.menu-toggle span {
  width: 100%;
  height: 3px;
  background-color: #d1c06e;
  border-radius: 3px;
  transition: all 0.3s ease;
}

.mobile-menu {
  display: none;
  position: fixed;
  top: 0;
  right: -100%;
  width: 80%;
  max-width: 300px;
  height: 100vh;
  background: white;
  box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
  padding-top: 80px;
  transition: right 0.3s ease;
  flex-direction: column;
  z-index: 99;
}

.mobile-menu.active {
  right: 0;
}

.mobile-link {
  display: block;
  padding: 15px 25px;
  color: #333;
  text-decoration: none;
  font-weight: 600;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s ease;
}

.mobile-link:hover {
  background-color: rgba(209, 192, 110, 0.1);
  color: #d1c06e;
  padding-left: 30px;
}

.mobile-link.active {
  background-color: rgba(209, 192, 110, 0.2);
  color: #d1c06e;
  border-left: 4px solid #d1c06e;
  padding-left: 26px;
}

@media (max-width: 768px) {
  .navbar-links {
    display: none;
  }
  
  .menu-toggle {
    display: flex;
  }
  
  .mobile-menu {
    display: flex;
  }
}

@keyframes fadeInLeft {
  0% {
    opacity: 0;
    transform: translateX(-20px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeInRight {
  0% {
    opacity: 0;
    transform: translateX(20px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

.demo-nav-btn {
  background: linear-gradient(45deg, #d1c06e, #e0d394);
  color: white;
  border: none;
  border-radius: 30px;
  padding: 0.6rem 1.2rem;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(209, 192, 110, 0.4);
  position: relative;
  overflow: hidden;
}

.demo-nav-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(209, 192, 110, 0.6);
  background: linear-gradient(45deg, #beab4f, #d1c06e);
}

.demo-nav-btn:active {
  transform: translateY(-1px);
}

.demo-nav-btn i {
  font-size: 1.1rem;
}

.demo-nav-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: 0.5s;
}

.demo-nav-btn:hover::before {
  left: 100%;
}

.demo-mobile-btn {
  background: linear-gradient(45deg, #d1c06e, #e0d394);
  color: white;
  border: none;
  border-radius: 30px;
  padding: 1rem 1.5rem;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 4px 15px rgba(209, 192, 110, 0.4);
  margin-top: 1rem;
  width: 100%;
}

.demo-mobile-btn:hover {
  background: linear-gradient(45deg, #beab4f, #d1c06e);
}

.demo-mobile-btn i {
  font-size: 1.2rem;
}

@media (max-width: 992px) {
  .navbar-links {
    display: none;
  }
  
  .menu-toggle {
    display: flex;
  }
  
  .mobile-menu {
    display: flex;
  }
}
/* ---- Client Portal CTA ---- */
.portal-nav-btn {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0.65rem 1.3rem;
  border: none;
  border-radius: 30px;
  background: linear-gradient(135deg, #00755e 0%, #019277 100%);
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.3px;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 117, 94, 0.35);
  transition: transform 0.35s cubic-bezier(0.34, 1.4, 0.64, 1),
              box-shadow 0.35s ease,
              background 0.35s ease;
}

/* Idle light sweep - slow enough to read as polish, not as a banner ad */
.portal-nav-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -130%;
  width: 60%;
  height: 100%;
  background: linear-gradient(
    100deg,
    transparent,
    rgba(255, 255, 255, 0.38),
    transparent
  );
  transform: skewX(-20deg);
  animation: portal-sheen 5.5s ease-in-out infinite;
}

@keyframes portal-sheen {
  0%,
  8% {
    left: -130%;
  }
  45%,
  100% {
    left: 170%;
  }
}

.portal-nav-btn:hover,
.portal-nav-btn:focus-visible {
  background: linear-gradient(135deg, #005f4c 0%, #00755e 100%);
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0, 117, 94, 0.45);
}

.portal-nav-btn:focus-visible {
  outline: 2px solid #00755e;
  outline-offset: 3px;
}

.portal-nav-btn:active {
  transform: translateY(-1px);
}

.portal-nav-btn i {
  font-size: 1.1rem;
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.portal-nav-btn:hover i {
  transform: rotate(14deg) scale(1.12);
}

.mobile-cta-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem 25px 0;
}

.mobile-cta-group .demo-mobile-btn {
  margin-top: 0;
}

.portal-mobile-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 30px;
  background: linear-gradient(135deg, #00755e 0%, #019277 100%);
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 117, 94, 0.3);
  transition: background 0.3s ease, box-shadow 0.3s ease;
}

.portal-mobile-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -130%;
  width: 60%;
  height: 100%;
  background: linear-gradient(
    100deg,
    transparent,
    rgba(255, 255, 255, 0.38),
    transparent
  );
  transform: skewX(-20deg);
  animation: portal-sheen 5.5s ease-in-out infinite;
}

.portal-mobile-btn:hover,
.portal-mobile-btn:focus-visible {
  background: linear-gradient(135deg, #005f4c 0%, #00755e 100%);
  box-shadow: 0 6px 20px rgba(0, 117, 94, 0.4);
}

.portal-mobile-btn:focus-visible {
  outline: 2px solid #00755e;
  outline-offset: 3px;
}

.portal-mobile-btn i {
  font-size: 1.2rem;
}

@media (prefers-reduced-motion: reduce) {
  .portal-nav-btn::before,
  .portal-mobile-btn::before {
    animation: none;
    opacity: 0;
  }

  .portal-nav-btn,
  .portal-nav-btn i {
    transition: background 0.2s ease;
  }

  .portal-nav-btn:hover,
  .portal-nav-btn:focus-visible {
    transform: none;
  }

  .portal-nav-btn:hover i {
    transform: none;
  }
}

/* Eight links plus two CTAs need two tiers of compaction to stay on one
   line all the way down to the 992px drawer breakpoint. */
@media (max-width: 1400px) and (min-width: 993px) {
  .navbar {
    padding: 1rem 1.5rem;
  }

  .navbar-links {
    gap: 0.75rem;
  }

  .nav-link {
    padding: 0.6rem 0.7rem;
    letter-spacing: 0;
  }

  .logo {
    height: 65px;
  }

  .company-name {
    font-size: 1.15rem;
  }
}

@media (max-width: 1150px) and (min-width: 993px) {
  .navbar {
    padding: 1rem 1rem;
  }

  .navbar-links {
    gap: 0.3rem;
  }

  .nav-link {
    padding: 0.55rem 0.5rem;
    font-size: 0.85rem;
  }

  .company-name {
    display: none;
  }

  .logo {
    height: 55px;
  }

  .portal-nav-btn,
  .demo-nav-btn {
    padding: 0.55rem 0.85rem;
    font-size: 0.8rem;
  }

  .portal-nav-btn i,
  .demo-nav-btn i {
    font-size: 0.95rem;
  }
}
</style>
